/**
 * 页面按钮权限汇总配置（统一按钮显示控制）
 * ------------------------------------------------------------------
 * 所有页面「按钮显示控制」相关的配置集中在本文件，改这里即可全局生效，
 * 无需去各个页面逐个修改。
 *
 * 核心能力：
 * 1. ACTION_GRANTS：页面逻辑权限名 与 后端菜单返回字段名 的映射
 *    —— 后端把按钮字段改名后，只需在此补充映射，页面 can('xxx') 无需改动
 * 2. PAGE_BUTTON_PERMISSIONS：各页面按钮权限汇总 + 显隐开关
 *    —— false 强制隐藏，true/未配置 跟随后端权限
 *
 * 页面使用方式（无需在页面里单独引入本文件）：
 *   const { can, canAny } = useButtonPermission('/页面路径/index.vue')
 *   内部自动引用本配置。
 * ------------------------------------------------------------------
 */

/**
 * 权限映射表：逻辑权限名 -> 后端菜单可能返回的按钮字段名
 * ------------------------------------------------------------------
 * key   : 页面代码中 can('xxx') / canAny('xxx') 使用的逻辑权限名
 * value : 后端菜单返回的按钮字段名集合（匹配任意一个即认为有权限）
 *
 * 场景举例：
 *   后端把"编辑"按钮字段名改成了"编辑2"，页面里仍是 can('编辑')。
 *   只需在此配置 编辑: ['编辑', '编辑2']，所有页面的编辑按钮自动恢复正常，
 *   无需去每个页面改代码。
 * ------------------------------------------------------------------
 */
export const ACTION_GRANTS: Record<string, string[]> = {
  新增: ['新增', '新增与编辑', '添加角色'],
  编辑: ['编辑', '新增与编辑', '更新', '修改角色'],
  保存: ['保存', '保存分配', '新增', '新增与编辑', '编辑', '更新', '添加角色', '修改角色'],
  删除: ['删除', '刪除', '删除角色'],
  详情: ['详情', '项目详情'],
  查看: ['查看', '详情', '项目详情'],
  查询: ['查询', '列表'],
  刷新: ['查询', '列表'],
}

/** 页面路径 -> 按钮权限名 -> 是否显示（false 强制隐藏，true 跟随后端权限） */
export const PAGE_BUTTON_PERMISSIONS: Record<string, Record<string, boolean>> = {
  // ============ 可视化管理 ============
  '/visualModel/QuestionAnswerAgent/index.vue': {
    新增: true,
    查询: true,
    编辑: true,
    删除: true,
    保存: true,
  },
  '/visualModel/DeviceReadingDataset/index.vue': {
    查询: true,
    照片查看: true,
  },
  '/visualModel/DeviceReadingAgent/index.vue': {
    新增: true,
    查询: true,
    编辑: true,
    删除: true,
    保存: true,
  },

  // ============ 原始数据管理 ============
  '/RawDataManagement/OriginalDetectionList/index.vue': {
    详情: true,
    现场照片下载: true,
    原始数据excel下载: true,
    数据变更: true,
  },
  '/RawDataManagement/ChangeRecords/index.vue': {
    详情: true,
  },
  '/RawDataManagement/AnalysisOfBusiness/index.vue': {
    列表: true,
    详情: true,
    打印: true,
    设置人员: true,
    撤回项目: true,
    完成项目: true,
    汇总查询: true,
    项目进度分析: true,
  },

  // ============ 权限管理 ============
  '/QuanXianGuanLi/juese.vue': {
    新增: true,
    编辑: true,
    删除: true,
    保存: true,
  },
  '/QuanXianGuanLi/jiekou.vue': {
    新增: true,
    列表: true,
    编辑: true,
    删除: true,
    保存: true,
  },
  '/QuanXianGuanLi/fenpei.vue': {
    保存分配: true,
  },
  '/QuanXianGuanLi/caidan.vue': {
    列表: true,
    新增: true,
    编辑: true,
    删除: true,
    保存: true,
  },

  // ============ 参数设置 ============
  '/ParameterSettings/TestItemPackage/index.vue': {
    新增: true,
    查看: true,
    删除: true,
    编辑: true,
    保存: true,
  },
  '/ParameterSettings/tableEditing/index.vue': {
    新增: true,
    查看: true,
    删除: true,
    编辑: true,
    打印: true,
    保存: true,
  },
  '/ParameterSettings/BusinessScope/index.vue': {
    新增: true,
    列表: true,
    快速同步: true,
    项目详情: true,
    编辑: true,
    图片上传: true,
    保存: true,
  },

  // ============ 开发管理 ============
  '/KaiFa/AppVersionAdmin/index.vue': {
    上传: true,
    下载: true,
  },
}

/** 查询某页面配置了哪些按钮权限 */
export const getPageButtonPermissions = (componentPath: string): string[] => {
  const normalized = componentPath.replace(/^\/+/, '')
  const page = PAGE_BUTTON_PERMISSIONS[`/${normalized}`]
  return page ? Object.keys(page) : []
}

/**
 * 查询某按钮权限在配置文件中的显隐状态
 * @returns true 显示 / false 强制隐藏 / undefined 未配置（跟随后端权限）
 */
export const getPageButtonVisibility = (componentPath: string, action: string): boolean | undefined => {
  const normalized = componentPath.replace(/^\/+/, '')
  const page = PAGE_BUTTON_PERMISSIONS[`/${normalized}`]
  if (!page) return undefined
  return page[action]
}

/** 查询某个按钮权限在哪些页面被使用 */
export const getPagesByButtonPermission = (action: string): string[] =>
  Object.entries(PAGE_BUTTON_PERMISSIONS)
    .filter(([, actions]) => action in actions)
    .map(([page]) => page)
